import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Lock, Mail, Eye, EyeOff } from 'lucide-react';

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await login(email, password);
      toast.success('Login successful');
      navigate('/admin');
    } catch (error) {
      toast.error(error.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-islamic-pearl flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-xl bg-islamic-teal flex items-center justify-center mx-auto mb-4 shadow-lg">
            <span className="text-white font-arabic text-2xl font-bold">ر</span>
          </div>
          <h1 className="font-serif text-2xl font-bold text-stone-800">Ar-Rahman Admin</h1>
          <p className="text-stone-500 text-sm">București, România</p>
        </div>

        {/* Login Card */}
        <Card className="border-stone-200 shadow-lg">
          <CardContent className="p-6 md:p-8">
            <h2 className="text-xl font-semibold text-stone-800 mb-6 text-center">
              {t('nav.login')}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-stone-400" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="admin@musulman.ro"
                    className="pl-10"
                    required
                    data-testid="login-email"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="password">Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-stone-400" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="pl-10 pr-10"
                    required
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-stone-400 hover:text-stone-600"
                    data-testid="toggle-password"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-islamic-teal hover:bg-islamic-teal-light"
                data-testid="login-submit"
              >
                {loading ? t('common.loading') : t('nav.login')}
              </Button>
            </form>

            {/* Demo credentials hint */}
            <div className="mt-6 p-4 bg-stone-50 rounded-lg">
              <p className="text-xs text-stone-500 text-center">
                First time? Register a new admin account using the API.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Back to site */}
        <div className="text-center mt-6">
          <a href="/" className="text-sm text-islamic-teal hover:underline">
            ← {t('common.back')} to site
          </a>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginPage;
